import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CAMPAIGN_ADMIN_USER_PICKER } from '@/queries/queries';

export interface AdminUserOption {
  id: string;
  name: string | null;
  email: string;
}

interface AdminUserPickerProps {
  value: AdminUserOption | null;
  onChange: (user: AdminUserOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

const formatOption = (u: AdminUserOption) =>
  u.name ? `${u.name} — ${u.email}` : u.email;

export function AdminUserPicker({
  value,
  onChange,
  placeholder,
  disabled,
  id,
}: AdminUserPickerProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const { data } = useQuery(CAMPAIGN_ADMIN_USER_PICKER, {
    variables: { search, pageSize: 10 },
    skip: !open,
  });
  const options: AdminUserOption[] = data?.adminUsers?.data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className="w-full justify-start"
          disabled={disabled}
        >
          {value ? formatOption(value) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[400px]">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>—</CommandEmpty>
            <CommandGroup>
              {options.map((u) => (
                <CommandItem
                  key={u.id}
                  onSelect={() => {
                    onChange({ id: u.id, name: u.name, email: u.email });
                    setOpen(false);
                  }}
                >
                  {formatOption(u)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
